import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";
import { getTeamMemberMessagePreviewLabel } from "../../../utils/teamMemberChatPreview";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Debounce for fetchTeamMemberChats — called from mount AND socket events.
const chatDebounceMap = new Map();
const CHAT_DEBOUNCE_TIME = 5000;
const shouldDebounceChats = () => {
  const now = Date.now();
  const last = chatDebounceMap.get('fetchTeamMemberChats');
  if (last && (now - last) < CHAT_DEBOUNCE_TIME) return true;
  chatDebounceMap.set('fetchTeamMemberChats', now);
  return false;
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetTeamMemberChatCaches = () => {
  chatDebounceMap.clear();
};

// Helper function to get status precedence (same pattern as user conversations)
const getStatusPrecedence = (status) => {
  const precedence = {
    seen: 4,
    delivered: 2,
    sent: 1,
    pending: 0,
    failed: -1,
  };
  return precedence[status] ?? -1;
};

// Helper function to preserve more advanced status (same pattern as user conversations)
const preserveAdvancedStatus = (existingStatus, newStatus) => {
  if (!existingStatus) return newStatus;
  if (!newStatus) return existingStatus;
  
  const existingPrecedence = getStatusPrecedence(existingStatus);
  const newPrecedence = getStatusPrecedence(newStatus);
  
  // Use the status with higher precedence (more advanced)
  return newPrecedence > existingPrecedence ? newStatus : existingStatus;
};

// Async thunk for fetching team member chats
export const fetchTeamMemberChats = createAsyncThunk(
  "teamMemberChat/fetchChats",
  async (_, { rejectWithValue, getState }) => {
    if (shouldDebounceChats()) return rejectWithValue("Debounced");

    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(
        `${SERVER_DOMAIN}/api/team-member-chats/chats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.data || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk for fetching messages between current user and another team member
export const fetchTeamMemberChatMessages = createAsyncThunk(
  "teamMemberChat/fetchMessages",
  async ({ otherMemberId, limit = 50, offset = 0 }, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.get(
        `${SERVER_DOMAIN}/api/team-member-chats/chats/${otherMemberId}/messages`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: { limit, offset },
        }
      );
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk for loading older messages (pagination) - similar to fetchOlderMessages for user conversations
export const fetchOlderTeamMemberMessages = createAsyncThunk(
  "teamMemberChat/fetchOlderMessages",
  async ({ otherMemberId, limit = 50 }, { rejectWithValue, getState, dispatch }) => {
    const state = getState();
    const currentChat = state.teamMemberChat?.currentChat;
    
    // Get current offset from state
    // Offset represents how many messages from the end we've already loaded
    // offset=0 means we're showing the last 50 messages
    // To load older, we increase offset by limit
    const currentOffset = currentChat?.offset || 0;
    const totalMessages = currentChat?.totalMessages || 0;
    
    // Check if there are more messages to load
    if (currentOffset + limit >= totalMessages) {
      return rejectWithValue("No older messages available");
    }
    
    // Calculate new offset (increase by limit to go further back)
    const newOffset = currentOffset + limit;
    
    // Dispatch the fetchTeamMemberChatMessages thunk with new offset
    const result = await dispatch(fetchTeamMemberChatMessages({ 
      otherMemberId, 
      limit, 
      offset: newOffset 
    }));
    
    return result.payload || result;
  }
);

// Async thunk for sending a message
export const sendTeamMemberMessage = createAsyncThunk(
  "teamMemberChat/sendMessage",
  async ({ otherMemberId, message, messageType = "text", replyTo, replyBody, replyType, tempId, documentFileName }, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      const response = await axios.post(
        `${SERVER_DOMAIN}/api/team-member-chats/chats/${otherMemberId}/send`,
        {
          message,
          messageType,
          replyTo,
          replyBody,
          replyType,
          tempId,
          ...(documentFileName ? { documentFileName } : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data.data.message;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Async thunk for marking chat as read
export const markTeamMemberChatAsRead = createAsyncThunk(
  "teamMemberChat/markAsRead",
  async (otherMemberId, { rejectWithValue }) => {
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    try {
      await axios.put(
        `${SERVER_DOMAIN}/api/team-member-chats/chats/${otherMemberId}/read`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return otherMemberId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Initial state
const initialState = {
  chats: [], // List of team member chats
  currentChat: {
    otherMemberId: null,
    otherMember: null,
    messages: [],
    loading: false,
    error: null,
    hasMore: false,
    totalMessages: 0,
    offset: 0, // Track pagination offset
  },
  unreadCounts: {}, // Map of memberId -> unread count
  loading: false,
  error: null,
};

// Slice
const teamMemberChatSlice = createSlice({
  name: "teamMemberChat",
  initialState,
  reducers: {
    setCurrentChat: (state, action) => {
      state.currentChat.otherMemberId = action.payload.otherMemberId;
      state.currentChat.otherMember = action.payload.otherMember || null;
      if (!action.payload.keepMessages) {
        state.currentChat.messages = [];
        state.currentChat.offset = 0; // Reset offset when switching chats
        state.currentChat.loading = false; // Reset loading state when switching chats
        state.currentChat.error = null; // Clear any previous errors
      }
    },
    clearCurrentChat: (state) => {
      state.currentChat = {
        ...initialState.currentChat,
        offset: 0, // Ensure offset is reset
      };
    },
    clearError: (state) => {
      state.currentChat.error = null;
    },
    addMessage: (state, action) => {
      const { message, otherMemberId } = action.payload;
      
      // Add to current chat if it matches
      if (state.currentChat.otherMemberId === otherMemberId) {
        // CRITICAL: More robust duplicate check - check all combinations (same pattern as user conversations)
        const messageId = message.id || message.tempId;
        if (messageId && state.currentChat.messages) {
          const existingIndex = state.currentChat.messages.findIndex(
            msg => {
              const msgId = msg.id || msg.tempId;
              // Check if IDs match
              if (msgId && messageId && msgId === messageId) return true;
              // Check if msg.id matches message.tempId
              if (msg.id && message.tempId && msg.id === message.tempId) return true;
              // Check if msg.tempId matches message.id
              if (msg.tempId && message.id && msg.tempId === message.id) return true;
              return false;
            }
          );
          
          if (existingIndex !== -1) {
            // Update existing message (in case socket event has more complete data)
            const existingMsg = state.currentChat.messages[existingIndex];
            state.currentChat.messages[existingIndex] = {
              ...existingMsg,
              ...message,
              // Preserve critical fields from existing message
              tempId: existingMsg.tempId || message.tempId,
              fromMe: existingMsg.fromMe !== undefined ? existingMsg.fromMe : message.fromMe,
              // CRITICAL: Preserve more advanced status (same pattern as user conversations)
              status: preserveAdvancedStatus(existingMsg.status, message.status),
            };
            // Don't add duplicate, just update
            // Continue to update chat list below
          } else {
            // No duplicate found by ID, add the message
            state.currentChat.messages.push(message);
          }
        } else {
          // No message ID, add it (shouldn't happen, but safe fallback)
          state.currentChat.messages.push(message);
        }
      }

      // Update chat list
      const chatIndex = state.chats.findIndex(
        chat => chat.otherMemberId === otherMemberId
      );
      if (chatIndex >= 0) {
        state.chats[chatIndex].lastMessage = {
          content: getTeamMemberMessagePreviewLabel(message),
          timestamp: message.timestamp,
          senderId: message.senderId,
          messageType: message.messageType || "text",
        };
        state.chats[chatIndex].updatedAt = new Date().toISOString();
        
        // Update unread count only when this thread is NOT currently open.
        // If the chat is open, keep unread at 0 to match read-as-open behavior.
        if (!message.fromMe) {
          const isCurrentOpenChat = state.currentChat.otherMemberId === otherMemberId;
          if (isCurrentOpenChat) {
            state.chats[chatIndex].unreadCount = 0;
            state.unreadCounts[otherMemberId] = 0;
          } else {
            state.chats[chatIndex].unreadCount = (state.chats[chatIndex].unreadCount || 0) + 1;
            state.unreadCounts[otherMemberId] = (state.unreadCounts[otherMemberId] || 0) + 1;
          }
        }
      } else {
        // Add new chat to list
        state.chats.unshift({
          otherMemberId,
          otherMemberName: message.senderName,
          lastMessage: {
            content: getTeamMemberMessagePreviewLabel(message),
            timestamp: message.timestamp,
            senderId: message.senderId,
            messageType: message.messageType || "text",
          },
          unreadCount: message.fromMe ? 0 : 1,
          updatedAt: new Date().toISOString(),
        });
        if (!message.fromMe) {
          const isCurrentOpenChat = state.currentChat.otherMemberId === otherMemberId;
          state.unreadCounts[otherMemberId] = isCurrentOpenChat ? 0 : 1;
          if (isCurrentOpenChat) {
            state.chats[0].unreadCount = 0;
          }
        }
      }
    },
    updateMessageStatus: (state, action) => {
      const { messageId, status } = action.payload;
      
      // Update in current chat - check both id and tempId
      const messageIndex = state.currentChat.messages.findIndex(
        msg => msg.id === messageId || msg.tempId === messageId
      );
      if (messageIndex >= 0) {
        const existingMessage = state.currentChat.messages[messageIndex];
        const currentStatus = existingMessage.status;
        
        // CRITICAL: Only update if new status has higher precedence (same pattern as user conversations)
        const currentPrecedence = getStatusPrecedence(currentStatus);
        const newPrecedence = getStatusPrecedence(status);
        
        if (newPrecedence > currentPrecedence) {
          state.currentChat.messages[messageIndex].status = status;
        }
        // If new status has same or lower precedence, don't update (preserve more advanced status)
      }
    },
    updateUnreadCount: (state, action) => {
      const { otherMemberId, count } = action.payload;
      const chatIndex = state.chats.findIndex(
        chat => chat.otherMemberId === otherMemberId
      );
      if (chatIndex >= 0) {
        state.chats[chatIndex].unreadCount = count;
      }
      state.unreadCounts[otherMemberId] = count;
    },
    updateInboxChat: (state, action) => {
      const { otherMemberId, lastMessage, unreadCount } = action.payload;
      const chatIndex = state.chats.findIndex(
        chat => chat.otherMemberId === otherMemberId
      );
      const isCurrentOpenChat = state.currentChat.otherMemberId === otherMemberId;
      if (chatIndex >= 0) {
        if (lastMessage) {
          state.chats[chatIndex].lastMessage = lastMessage;
        }
        if (unreadCount !== undefined) {
          const nextUnread = isCurrentOpenChat ? 0 : unreadCount;
          state.chats[chatIndex].unreadCount = nextUnread;
          state.unreadCounts[otherMemberId] = nextUnread;
        }
        state.chats[chatIndex].updatedAt = new Date().toISOString();
      } else if (lastMessage) {
        // Add new chat
        state.chats.unshift({
          otherMemberId,
          lastMessage,
          unreadCount: isCurrentOpenChat ? 0 : (unreadCount || 0),
          updatedAt: new Date().toISOString(),
        });
        state.unreadCounts[otherMemberId] = isCurrentOpenChat ? 0 : (unreadCount || 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch chats
      .addCase(fetchTeamMemberChats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeamMemberChats.fulfilled, (state, action) => {
        state.loading = false;
        state.chats = action.payload;
        // Update unread counts
        action.payload.forEach(chat => {
          state.unreadCounts[chat.otherMemberId] = chat.unreadCount || 0;
        });
      })
      .addCase(fetchTeamMemberChats.rejected, (state, action) => {
        state.loading = false;
        if (action.payload !== "Debounced") {
          state.error = action.payload;
        }
      })
      // Fetch messages
      .addCase(fetchTeamMemberChatMessages.pending, (state) => {
        state.currentChat.loading = true;
        state.currentChat.error = null;
      })
      .addCase(fetchTeamMemberChatMessages.fulfilled, (state, action) => {
        state.currentChat.loading = false;
        state.currentChat.error = null; // Clear any previous errors
        // Check if messages array is empty (initial load)
        const isInitialLoad = state.currentChat.messages.length === 0;
        
        if (isInitialLoad) {
          // Initial load: replace messages and reset offset
          // BUT: Merge with any socket messages that arrived before API response (same pattern as user conversations)
          const apiMessages = action.payload.messages || [];
          const existingMessages = state.currentChat.messages || [];
          
          if (existingMessages.length > 0) {
            // Merge socket messages with API messages (same pattern as user conversations)
            const apiMessageIds = new Set(
              apiMessages.map(msg => msg.id || msg.tempId).filter(Boolean)
            );
            
            // Add socket messages that aren't in the API response
            const socketOnlyMessages = existingMessages.filter(msg => {
              const msgId = msg.id || msg.tempId;
              return msgId && !apiMessageIds.has(msgId);
            });
            
            // Merge and sort: API messages + socket-only messages
            const mergedMessages = [...apiMessages, ...socketOnlyMessages].sort((a, b) => {
              const timeA = new Date(a.timestamp || 0).getTime();
              const timeB = new Date(b.timestamp || 0).getTime();
              return timeA - timeB; // Ascending order (oldest first)
            });
            
            state.currentChat.messages = mergedMessages;
            state.currentChat.offset = mergedMessages.length;
          } else {
            // CRITICAL: Even if apiMessages is empty, set it to ensure loading state is cleared
            state.currentChat.messages = apiMessages;
            state.currentChat.offset = apiMessages.length;
          }
        } else {
          // Loading older messages: prepend to existing messages (older messages go at the beginning)
          // CRITICAL: Deduplicate messages to prevent duplicates from socket events (same pattern as user conversations)
          const newMessages = action.payload.messages || [];
          const existingIds = new Set(
            state.currentChat.messages.map(msg => msg.id || msg.tempId).filter(Boolean)
          );
          
          // Filter out messages that already exist (same pattern as user conversations)
          const uniqueNewMessages = newMessages.filter(msg => {
            const msgId = msg.id || msg.tempId;
            return !msgId || !existingIds.has(msgId);
          });
          
          // Only prepend if there are unique new messages
          if (uniqueNewMessages.length > 0) {
            state.currentChat.messages = [...uniqueNewMessages, ...state.currentChat.messages];
            // Update offset: add the number of new messages loaded
            state.currentChat.offset = (state.currentChat.offset || 0) + (uniqueNewMessages.length || 0);
          }
        }
        
        // Update otherMember from API response if available, otherwise keep existing
        if (action.payload.otherMember) {
          state.currentChat.otherMember = action.payload.otherMember;
        }
        // Ensure otherMemberId is set if we have otherMember but not otherMemberId
        if (action.payload.otherMember?.id && !state.currentChat.otherMemberId) {
          state.currentChat.otherMemberId = action.payload.otherMember.id;
        }
        state.currentChat.hasMore = action.payload.hasMore || false;
        state.currentChat.totalMessages = action.payload.totalMessages || 0;
      })
      .addCase(fetchTeamMemberChatMessages.rejected, (state, action) => {
        state.currentChat.loading = false;
        state.currentChat.error = action.payload;
      })
      // Fetch older messages (pagination)
      .addCase(fetchOlderTeamMemberMessages.pending, (state) => {
        // Don't set loading to true here to avoid showing full screen loader
        // We'll use a separate loading state in the component
      })
      .addCase(fetchOlderTeamMemberMessages.fulfilled, (state, action) => {
        // Older messages are already handled by fetchTeamMemberChatMessages.fulfilled
        // This case is here for consistency and potential future use
      })
      .addCase(fetchOlderTeamMemberMessages.rejected, (state, action) => {
        // Handle error if needed
      })
      // Send message
      .addCase(sendTeamMemberMessage.fulfilled, (state, action) => {
        // Message is added optimistically and via socket, so we should NOT add it here
        // Only update if it exists (to sync server data), but don't add new messages
        const message = action.payload;
        const otherMemberId = message.receiverId;
        
        // Add to current chat if it matches
        if (state.currentChat.otherMemberId === otherMemberId) {
          // Check if message already exists (avoid duplicates) - check both id and tempId
          const existingIndex = state.currentChat.messages.findIndex(
            msg => (msg.id && message.id && msg.id === message.id) || 
                   (msg.tempId && message.tempId && msg.tempId === message.tempId) ||
                   (msg.id && message.tempId && msg.id === message.tempId) ||
                   (msg.tempId && message.id && msg.tempId === message.id)
          );
          
          if (existingIndex >= 0) {
            // Update existing message with server data (from optimistic update or socket)
            const existingMsg = state.currentChat.messages[existingIndex];
            state.currentChat.messages[existingIndex] = {
              ...existingMsg,
              ...message,
              // Preserve critical fields from existing message
              tempId: existingMsg.tempId || message.tempId,
              fromMe: existingMsg.fromMe !== undefined ? existingMsg.fromMe : message.fromMe,
              // CRITICAL: Preserve more advanced status (same pattern as user conversations)
              status: preserveAdvancedStatus(existingMsg.status, message.status),
            };
          }
          // CRITICAL: Don't add if it doesn't exist - it will come via socket or is already there optimistically
        }
        
        // CRITICAL: Also update chat list (not just currentChat)
        const chatIndex = state.chats.findIndex(
          chat => chat.otherMemberId === otherMemberId
        );
        if (chatIndex >= 0) {
          state.chats[chatIndex].lastMessage = {
            content: getTeamMemberMessagePreviewLabel(message),
            timestamp: message.timestamp,
            senderId: message.senderId,
            messageType: message.messageType || "text",
          };
          state.chats[chatIndex].updatedAt = new Date().toISOString();
        } else {
          // Add new chat to list if it doesn't exist
          state.chats.unshift({
            otherMemberId,
            otherMemberName: message.senderName,
            lastMessage: {
              content: getTeamMemberMessagePreviewLabel(message),
              timestamp: message.timestamp,
              senderId: message.senderId,
              messageType: message.messageType || "text",
            },
            unreadCount: 0, // Sent by current user, so unread count is 0
            updatedAt: new Date().toISOString(),
          });
        }
      })
      // Mark as read
      .addCase(markTeamMemberChatAsRead.fulfilled, (state, action) => {
        const otherMemberId = action.payload;
        const chatIndex = state.chats.findIndex(
          chat => chat.otherMemberId === otherMemberId
        );
        if (chatIndex >= 0) {
          state.chats[chatIndex].unreadCount = 0;
        }
        state.unreadCounts[otherMemberId] = 0;
        
        // Update message statuses to "seen" in current chat
        if (state.currentChat.otherMemberId === otherMemberId) {
          state.currentChat.messages.forEach(msg => {
            if (!msg.fromMe && msg.status !== "seen") {
              msg.status = "seen";
            }
          });
        }
      });
  },
});

export const {
  setCurrentChat,
  clearCurrentChat,
  clearError,
  addMessage,
  updateMessageStatus,
  updateUnreadCount,
  updateInboxChat,
} = teamMemberChatSlice.actions;

export default teamMemberChatSlice.reducer;
